import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlexaSettings } from "../components/AlexaSettings.js";
import { SyncSettings } from "../components/SyncSettings.js";
import * as api from "../lib/api.js";
import * as storage from "../lib/storage.js";
import type { StoredList } from "../types/index.js";
import styles from "./ListsPage.module.css";

interface Props {
  lists: StoredList[];
  syncAccountId: string | null;
  onListsChange: () => void;
  onSyncChange: (syncAccountId: string | null) => void;
}

export function ListsPage({ lists, syncAccountId, onListsChange, onSyncChange }: Props) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [shareUrlCopied, setShareUrlCopied] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const displayName = storage.getDisplayName();

  const handleCreate = useCallback(async () => {
    const name = newName.trim();
    if (!name) return;

    setCreating(true);
    try {
      const result = await api.createList(name, displayName);
      storage.addList({
        listId: result.listId,
        shareToken: result.shareToken,
        name: result.list.name,
      });
      setNewName("");
      onListsChange();
    } catch (err) {
      console.error("Failed to create list:", err);
      setErrorMessage("リストの作成に失敗しました。もう一度お試しください。");
    } finally {
      setCreating(false);
    }
  }, [newName, displayName, onListsChange]);

  const handleSetPrimary = useCallback(
    (listId: string) => {
      storage.setPrimary(listId);
      onListsChange();
    },
    [onListsChange],
  );

  const handleRemove = useCallback(
    (listId: string) => {
      storage.removeList(listId);
      onListsChange();
    },
    [onListsChange],
  );

  const handleCopyShareUrl = useCallback(async (shareToken: string) => {
    const url = `${window.location.origin}/l/${shareToken}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareUrlCopied(shareToken);
      setTimeout(() => setShareUrlCopied(null), 2000);
    } catch {
      setErrorMessage("クリップボードへのコピーに失敗しました。");
    }
  }, []);

  const handleRotate = useCallback(
    async (list: StoredList) => {
      if (!confirm("共有URLを更新しますか？現在のURLは無効になります。")) return;
      try {
        const result = await api.rotateShareUrl(list.shareToken);
        storage.updateListShareToken(list.listId, result.newShareToken);
        onListsChange();
      } catch (err) {
        console.error("Failed to rotate:", err);
        setErrorMessage("URLの更新に失敗しました。もう一度お試しください。");
      }
    },
    [onListsChange],
  );

  return (
    <main className={styles.container}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/")}
          aria-label="ホームに戻る"
        >
          &larr;
        </button>
        <h1 className={styles.title}>マイリスト</h1>
      </header>

      <SyncSettings
        syncAccountId={syncAccountId}
        lists={lists}
        displayName={displayName}
        onSyncChange={onSyncChange}
      />

      <AlexaSettings
        shareToken={lists.find((l) => l.isPrimary)?.shareToken ?? null}
        displayName={displayName}
      />

      {errorMessage && (
        <div
          role="alert"
          style={{
            background: "#fef2f2",
            color: "#dc2626",
            padding: "0.5rem 1rem",
            borderRadius: "6px",
            marginBottom: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{errorMessage}</span>
          <button
            type="button"
            onClick={() => setErrorMessage(null)}
            aria-label="エラーを閉じる"
            style={{
              background: "none",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              fontSize: "1.2rem",
              padding: "0 0.25rem",
            }}
          >
            &times;
          </button>
        </div>
      )}

      <div className={styles.createForm}>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="新しいリスト名…"
          maxLength={50}
          aria-label="新しいリスト名"
        />
        <button type="button" onClick={handleCreate} disabled={!newName.trim() || creating}>
          作成
        </button>
      </div>

      <ul className={styles.list}>
        {lists.map((list) => (
          <li key={list.listId} className={styles.item}>
            <div className={styles.itemInfo}>
              <span className={styles.name}>
                {list.name || "名称未設定"}
                {list.isPrimary && <span className={styles.primaryBadge}>メイン</span>}
              </span>
            </div>
            <div className={styles.actions}>
              {!list.isPrimary && (
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => handleSetPrimary(list.listId)}
                >
                  メインにする
                </button>
              )}
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => handleCopyShareUrl(list.shareToken)}
              >
                <span aria-live="polite">
                  {shareUrlCopied === list.shareToken ? "コピー済み" : "共有"}
                </span>
              </button>
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => handleRotate(list)}
              >
                URL更新
              </button>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.danger}`}
                onClick={() => handleRemove(list.listId)}
              >
                削除
              </button>
            </div>
          </li>
        ))}
      </ul>

      {lists.length === 0 && (
        <p className={styles.empty}>リストはまだありません。上から作成しましょう！</p>
      )}
    </main>
  );
}
